import React, { useLayoutEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const LineChart = ({ chartData }) => {
  useLayoutEffect(() => {
    let root = am5.Root.new("lineChart");

    root._logo?.dispose();
    root.setThemes([am5themes_Animated.new(root)]);

    // Chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
        paddingTop: 30,
      })
    );

    // preprocess data
    const formattedData = chartData.map((item) => {
      let sourceUrl = item.source || "";
      let sourceName = "Open Source";
      try {
        const urlObj = new URL(sourceUrl);
        sourceName = urlObj.hostname.replace("www.", "");
      } catch (e) {
        if (sourceUrl) sourceName = "Source";
      }
      return { ...item, sourceUrl, sourceName };
    });

    // X Axis
    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "year",
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 40 }),
      })
    );
    xAxis.data.setAll(formattedData);

    // Y Axes
    let yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0,
      })
    );
    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: "R & D Spend (M$)",
        y: am5.p50,
        centerX: am5.p50,
      })
    );

    let yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, { opposite: true }),
        min: 0,
      })
    );
    // yAxisRight.children.unshift(
    //   am5.Label.new(root, {
    //     rotation: 90,
    //     text: "% of Revenue",
    //     y: am5.p50,
    //     centerX: am5.p50,
    //   })
    // );

    // Series
    let spendSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "R&D Spend",
        xAxis,
        yAxis: yAxisLeft,
        valueYField: "spend",
        categoryXField: "year",
        stroke: am5.color(0x4a67d6),
        maskBullets: false,
      })
    );

    spendSeries.strokes.template.setAll({ strokeWidth: 2 });
    spendSeries.data.setAll(formattedData);

    // Create tooltip as HTML div (persistent + copiable) with close button
    const htmlTooltip = document.createElement("div");
    htmlTooltip.style.position = "absolute";
    htmlTooltip.style.background = "#fff";
    htmlTooltip.style.color = "#000";
    htmlTooltip.style.padding = "8px 12px";
    htmlTooltip.style.border = "1px solid #ccc";
    htmlTooltip.style.borderRadius = "6px";
    htmlTooltip.style.fontSize = "13px";
    htmlTooltip.style.whiteSpace = "pre-wrap";
    htmlTooltip.style.pointerEvents = "auto"; // allow text selection / copy
    htmlTooltip.style.display = "none";
    htmlTooltip.style.zIndex = "1000";
    htmlTooltip.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
    htmlTooltip.style.maxWidth = "auto";

    // Create close button
    const closeBtn = document.createElement("button");
    closeBtn.textContent = "×";
    closeBtn.style.position = "absolute";
    closeBtn.style.top = "4px";
    closeBtn.style.right = "6px";
    closeBtn.style.background = "transparent";
    closeBtn.style.border = "none";
    closeBtn.style.fontSize = "16px";
    closeBtn.style.cursor = "pointer";
    closeBtn.style.color = "#999";

    closeBtn.addEventListener("click", () => {
      htmlTooltip.style.display = "none";
    });

    htmlTooltip.appendChild(closeBtn);
    document.body.appendChild(htmlTooltip);

    let activeCtx = null;

    spendSeries.bullets.push(() => {
      // let circle = am5.Circle.new(root, {
      //   radius: 5,
      //   fill: am5.color(0x5a4fcf),
      // });
      let container = am5.Container.new(root, {});

      let circle = am5.Circle.new(root, {
        radius: 5,
        fill: am5.color(0x5a4fcf),
      });

      let label = am5.Label.new(root, {
        text: "{valueY}",
        centerY: am5.p100,
        centerX: am5.p50,
        dy: -15,
        populateText: true,
        fontSize: 12,
        fill: am5.color(0x000000),
        oversizedBehavior: "wrap", // prevent cutting
        maxWidth: 50,
        paddingTop: 5, // optional fine-tuning
        marginTop: 10, // Add this line to push content down
      });

      container.children.push(circle);
      container.children.push(label);

      container.events.on("pointerover", (ev) => {
        const dataContext = ev.target.dataItem?.dataContext;
        if (!dataContext) return;

        activeCtx = dataContext;

        // Clear previous content but keep the close button
        htmlTooltip.innerHTML = "";
        htmlTooltip.appendChild(closeBtn);

        const content = document.createElement("div");
        content.innerHTML = `
          <div><strong>Source:</strong> ${activeCtx.source}</div>
          <div><strong>Reasoning:</strong> ${activeCtx.reasoning}</div>
        `;
        htmlTooltip.appendChild(content);

        htmlTooltip.style.display = "block";

        if (ev.originalEvent) {
          htmlTooltip.style.left = ev.originalEvent.pageX + "px";
          htmlTooltip.style.top = ev.originalEvent.pageY + "px";
          htmlTooltip.style.transform = "translate(-62.5%, -120%)";
        }
      });

      container.events.on("pointermove", (ev) => {
        if (ev.originalEvent) {
          htmlTooltip.style.left = ev.originalEvent.pageX + "px";
          htmlTooltip.style.top = ev.originalEvent.pageY + "px";
          htmlTooltip.style.transform = "translate(-50%, -120%)";
        }
      });

      return am5.Bullet.new(root, {
        sprite: container,
      });

      // circle.events.on("pointerover", (ev) => {
      //   activeCtx = circle.dataItem.dataContext;

      //   // Clear previous content but keep the close button
      //   htmlTooltip.innerHTML = "";
      //   htmlTooltip.appendChild(closeBtn);

      //   const content = document.createElement("div");
      //   content.innerHTML = `
      //     <div><strong>Source:</strong> ${activeCtx.source}</div>
      //     <div><strong>Reasoning:</strong> ${activeCtx.reasoning}</div>
      //   `;
      //   htmlTooltip.appendChild(content);

      //   htmlTooltip.style.display = "block";

      //   if (ev.originalEvent) {
      //     htmlTooltip.style.left = ev.originalEvent.pageX + "px";
      //     htmlTooltip.style.top = ev.originalEvent.pageY + "px";
      //     htmlTooltip.style.transform = "translate(-62.5%, -120%)";
      //   }
      // });

      // circle.events.on("pointermove", (ev) => {
      //   if (ev.originalEvent) {
      //     htmlTooltip.style.left = ev.originalEvent.pageX + "px";
      //     htmlTooltip.style.top = ev.originalEvent.pageY + "px";
      //     htmlTooltip.style.transform = "translate(-50%, -120%)";
      //   }
      // });

      // return am5.Bullet.new(root, {
      //   sprite: circle,
      // });
    });

    chart.appear(1000, 100);

    return () => {
      root.dispose();
      htmlTooltip.remove();
    };
  }, [chartData]);

  // return <div id="lineChart" style={{ width: "100%", height: "300px" }}></div>;
  return (
    <div
      id="lineChart"
      style={{
        width: "100%",
        height: "300px",
        overflow: "visible",
      }}
    ></div>
  );
};

export default LineChart;
